import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatMoney } from '@/lib/utils'
import type { MetodoPago } from '@/types/database'

// ─── Period helpers ───────────────────────────────────────────────────────────

type Periodo = 'hoy' | 'semana' | 'mes' | 'mes_anterior' | 'personalizado'

function getPeriodo(
  p: Periodo,
  customDesde?: string,
  customHasta?: string,
): { desde: string; hasta: string } {
  const now = new Date()
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate()
  switch (p) {
    case 'hoy':
      return { desde: new Date(y, m, d).toISOString(), hasta: new Date(y, m, d + 1).toISOString() }
    case 'semana': {
      const dow = now.getDay() === 0 ? 6 : now.getDay() - 1
      return { desde: new Date(y, m, d - dow).toISOString(), hasta: new Date(y, m, d + 1).toISOString() }
    }
    case 'mes':
      return { desde: new Date(y, m, 1).toISOString(), hasta: new Date(y, m + 1, 1).toISOString() }
    case 'mes_anterior':
      return { desde: new Date(y, m - 1, 1).toISOString(), hasta: new Date(y, m, 1).toISOString() }
    case 'personalizado':
      return {
        desde: customDesde ? new Date(customDesde).toISOString() : new Date(y, m, 1).toISOString(),
        hasta: customHasta ? new Date(new Date(customHasta).getTime() + 86400000).toISOString() : new Date(y, m + 1, 1).toISOString(),
      }
  }
}

function periodoLabel(p: Periodo, customDesde?: string, customHasta?: string): string {
  if (p === 'personalizado' && customDesde && customHasta) {
    return `${formatFechaCorta2(customDesde)} — ${formatFechaCorta2(customHasta)}`
  }
  const map: Record<Periodo, string> = {
    hoy: 'Hoy',
    semana: 'Esta semana',
    mes: 'Este mes',
    mes_anterior: 'Mes anterior',
    personalizado: 'Personalizado',
  }
  return map[p]
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReporteVentas {
  count: number; totalMonto: number; mayoristas: number
  ticket: number; totalMayorista: number; totalMinorista: number
}

interface TopProducto {
  producto_id: string; nombre: string; marca: string | null; unidades: number; total: number
}

interface DetalleVenta {
  id: string; numero: number; tipo: string; estado: string
  total: number; total_pagado: number; created_at: string
  clientes: { nombre: string } | null
}

interface CajaReporte {
  totalIngresos: number
  totalEgresos: number
  saldoNeto: number
  sesiones: number
  desglose: { tipo: string; concepto: string; total: number; cantidad: number }[]
}

// ─── Queries ─────────────────────────────────────────────────────────────────

function qs(desde: string, hasta: string) {
  return `?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`
}

function useReporteVentas(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['reporte-ventas', desde, hasta],
    queryFn: () => api.get<ReporteVentas>(`/api/reportes/ventas${qs(desde, hasta)}`),
    staleTime: 60_000,
  })
}

function usePagosPeriodo(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['reporte-pagos', desde, hasta],
    queryFn: () => api.get<Partial<Record<MetodoPago, number>>>(`/api/reportes/pagos${qs(desde, hasta)}`),
    staleTime: 60_000,
  })
}

function useTopProductos(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['reporte-top-productos', desde, hasta],
    queryFn: () => api.get<TopProducto[]>(`/api/reportes/top-productos${qs(desde, hasta)}`),
    staleTime: 60_000,
  })
}

function useComprasPeriodo(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['reporte-compras', desde, hasta],
    queryFn: () => api.get<{ count: number; totalMonto: number }>(`/api/reportes/compras${qs(desde, hasta)}`),
    staleTime: 60_000,
  })
}

function useDetalleVentas(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['reporte-detalle-ventas', desde, hasta],
    queryFn: () => api.get<DetalleVenta[]>(`/api/reportes/detalle-ventas${qs(desde, hasta)}&limit=500`),
    staleTime: 60_000,
  })
}

function useCajaReporte(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['reporte-caja', desde, hasta],
    queryFn: () => api.get<CajaReporte>(`/api/reportes/caja${qs(desde, hasta)}`),
    staleTime: 60_000,
  })
}

// ─── Constants ────────────────────────────────────────────────────────────────

const METODO_LABEL: Record<MetodoPago, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  debito: 'Débito',
  credito: 'Crédito',
  cuenta_corriente: 'Cta. Corriente',
}

const PERIODOS: { value: Periodo; label: string }[] = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mes' },
  { value: 'mes_anterior', label: 'Mes anterior' },
  { value: 'personalizado', label: 'Personalizado' },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFechaCorta(iso: string): string {
  const d = new Date(iso)
  return (
    d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
    + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  )
}

function formatFechaCorta2(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

// ─── Export functions ─────────────────────────────────────────────────────────

function exportarCSV(
  periodoStr: string,
  ventasData: ReporteVentas | undefined,
  comprasData: { count: number; totalMonto: number } | undefined,
  pagosData: Partial<Record<MetodoPago, number>> | undefined,
  topData: TopProducto[] | undefined,
  cajaData: CajaReporte | undefined,
  detalleData: DetalleVenta[] | undefined,
) {
  const sep = ';'
  const lines: string[] = []

  const row = (...cols: (string | number)[]) =>
    cols.map(c => `"${String(c).replace(/"/g, '""')}"`).join(sep)

  lines.push(row('REPORTE MAYOEXPRESS'), row(`Período: ${periodoStr}`), '')

  // KPIs
  lines.push(row('=== RESUMEN GENERAL ==='))
  lines.push(row('Concepto', 'Valor'))
  lines.push(row('Ventas realizadas', ventasData?.count ?? 0))
  lines.push(row('Total vendido', `$${formatMoney(ventasData?.totalMonto ?? 0)}`))
  lines.push(row('Ticket promedio', `$${formatMoney(ventasData?.ticket ?? 0)}`))
  lines.push(row('Ventas mayoristas', ventasData?.mayoristas ?? 0))
  lines.push(row('Total mayorista', `$${formatMoney(ventasData?.totalMayorista ?? 0)}`))
  lines.push(row('Total minorista', `$${formatMoney(ventasData?.totalMinorista ?? 0)}`))
  lines.push(row('Órdenes de compra', comprasData?.count ?? 0))
  lines.push(row('Total en compras', `$${formatMoney(comprasData?.totalMonto ?? 0)}`))
  lines.push('')

  // Medios de pago
  if (pagosData && Object.keys(pagosData).length > 0) {
    lines.push(row('=== MEDIOS DE PAGO ==='))
    lines.push(row('Método', 'Monto'))
    for (const [m, v] of Object.entries(pagosData) as [MetodoPago, number][]) {
      if (v > 0) lines.push(row(METODO_LABEL[m] ?? m, `$${formatMoney(v)}`))
    }
    lines.push('')
  }

  // Top productos
  if (topData && topData.length > 0) {
    lines.push(row('=== TOP PRODUCTOS ==='))
    lines.push(row('Pos', 'Producto', 'Marca', 'Unidades', 'Total'))
    topData.forEach((p, i) => {
      lines.push(row(i + 1, p.nombre, p.marca ?? '', p.unidades, `$${formatMoney(Number(p.total))}`))
    })
    lines.push('')
  }

  // Caja
  if (cajaData && cajaData.sesiones > 0) {
    lines.push(row('=== MOVIMIENTOS DE CAJA ==='))
    lines.push(row('Sesiones', cajaData.sesiones))
    lines.push(row('Total ingresos', `$${formatMoney(cajaData.totalIngresos)}`))
    lines.push(row('Total egresos', `$${formatMoney(cajaData.totalEgresos)}`))
    lines.push(row('Saldo neto', `$${formatMoney(cajaData.saldoNeto)}`))
    if (cajaData.desglose.length > 0) {
      lines.push('')
      lines.push(row('Tipo', 'Concepto', 'Movimientos', 'Total'))
      for (const d of cajaData.desglose) {
        lines.push(row(d.tipo === 'ingreso' ? 'Ingreso' : 'Egreso', d.concepto, d.cantidad, `$${formatMoney(d.total)}`))
      }
    }
    lines.push('')
  }

  // Detalle ventas
  if (detalleData && detalleData.length > 0) {
    lines.push(row('=== DETALLE DE VENTAS ==='))
    lines.push(row('Venta', 'Tipo', 'Cliente', 'Estado', 'Total', 'Cobrado', 'Fecha'))
    detalleData.forEach(v => {
      lines.push(row(
        `#${v.numero}`, v.tipo, v.clientes?.nombre ?? '',
        v.estado,
        v.total.toFixed(2).replace('.', ','),
        v.total_pagado.toFixed(2).replace('.', ','),
        formatFechaCorta(v.created_at),
      ))
    })
  }

  const csv = '﻿' + lines.join('\r\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `reporte-${periodoStr.replace(/[\s/]/g, '-')}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color }: {
  label: string; value: string; sub?: string; color?: 'ok' | 'bad' | 'warn'
}) {
  const valueColor =
    color === 'ok' ? 'var(--me-ok)' :
    color === 'bad' ? 'var(--me-bad)' :
    color === 'warn' ? 'var(--me-warn)' :
    'var(--me-text)'
  return (
    <div className="me-card" style={{ flex: '1 1 0', minWidth: 0 }}>
      <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>{label}</div>
      <div style={{
        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
        fontSize: 'var(--me-text-3xl)', fontWeight: 700, color: valueColor,
        lineHeight: 1, letterSpacing: '-0.02em',
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 'var(--me-text-xs)', color: 'var(--me-text-3)', marginTop: 'var(--me-space-2)' }}>
          {sub}
        </div>
      )}
    </div>
  )
}

function EstadoPill({ estado }: { estado: string }) {
  if (estado === 'pagada')        return <span className="me-pill me-pill-ok">pagada</span>
  if (estado === 'pago_parcial')  return <span className="me-pill me-pill-warn">parcial</span>
  if (estado === 'pendiente_pago') return <span className="me-pill me-pill-neutral">pendiente</span>
  if (estado === 'anulada')       return <span className="me-pill me-pill-bad">anulada</span>
  return <span className="me-pill me-pill-neutral">{estado}</span>
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ReportesPage() {
  const [periodo, setPeriodo] = useState<Periodo>('mes')
  const [customDesde, setCustomDesde] = useState('')
  const [customHasta, setCustomHasta] = useState('')
  const printRef = useRef<HTMLDivElement>(null)

  const { desde, hasta } = getPeriodo(periodo, customDesde, customHasta)

  const ventas       = useReporteVentas(desde, hasta)
  const pagos        = usePagosPeriodo(desde, hasta)
  const topProductos = useTopProductos(desde, hasta)
  const compras      = useComprasPeriodo(desde, hasta)
  const detalleVentas = useDetalleVentas(desde, hasta)
  const caja         = useCajaReporte(desde, hasta)

  const totalPagos = pagos.data ? Object.values(pagos.data).reduce((s, v) => s + (v ?? 0), 0) : 0
  const maxPago    = pagos.data ? Math.max(...Object.values(pagos.data).map(v => v ?? 0), 0.01) : 0.01
  const periodoStr = periodoLabel(periodo, customDesde, customHasta)

  const todosListos = !ventas.isLoading && !pagos.isLoading && !topProductos.isLoading
    && !compras.isLoading && !detalleVentas.isLoading && !caja.isLoading

  const handlePrint = () => window.print()

  const handleExportCSV = () => exportarCSV(
    periodoStr,
    ventas.data,
    compras.data,
    pagos.data,
    topProductos.data,
    caja.data,
    detalleVentas.data,
  )

  return (
    <div ref={printRef}>
      {/* ── Print header (only visible when printing) ──────────────────── */}
      <div className="print-only" style={{ marginBottom: 'var(--me-space-6)' }}>
        <div style={{ fontFamily: 'var(--me-display)', fontVariationSettings: '"opsz" 48, "wght" 700', fontSize: 22 }}>
          Reporte MayoExpress
        </div>
        <div style={{ fontSize: 'var(--me-text-sm)', color: 'var(--me-text-3)', marginTop: 4 }}>
          Período: {periodoStr}
        </div>
      </div>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="no-print" style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 'var(--me-space-6)', gap: 'var(--me-space-4)', flexWrap: 'wrap',
      }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Resumen financiero</div>
          <h1 className="me-page-title">Reportes</h1>
        </div>

        <div style={{ display: 'flex', gap: 'var(--me-space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Selector de período */}
          <div style={{
            display: 'flex', background: 'var(--me-surface)',
            border: '1px solid var(--me-line-2)', borderRadius: 10,
            overflow: 'hidden', flexShrink: 0,
          }}>
            {PERIODOS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriodo(p.value)}
                style={{
                  padding: '9px 14px', border: 'none',
                  background: periodo === p.value ? 'var(--me-acid)' : 'transparent',
                  color: periodo === p.value ? 'var(--me-acid-ink)' : 'var(--me-text-2)',
                  fontFamily: 'var(--me-sans)', fontSize: 'var(--me-text-sm)',
                  fontWeight: periodo === p.value ? 600 : 400,
                  cursor: 'pointer', transition: 'background var(--me-dur-short)', whiteSpace: 'nowrap',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Acciones */}
          <button
            className="me-btn me-btn-ghost me-btn-sm"
            onClick={handleExportCSV}
            disabled={!todosListos}
            title="Exportar todos los datos del período"
          >
            Exportar CSV
          </button>
          <button
            className="me-btn me-btn-ghost me-btn-sm"
            onClick={handlePrint}
            title="Imprimir o guardar como PDF"
          >
            Imprimir / PDF
          </button>
        </div>
      </div>

      {/* ── Rango personalizado ─────────────────────────────────────────── */}
      {periodo === 'personalizado' && (
        <div className="no-print me-card" style={{
          marginBottom: 'var(--me-space-6)',
          display: 'flex', alignItems: 'center', gap: 'var(--me-space-4)', flexWrap: 'wrap',
          padding: 'var(--me-space-4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--me-space-2)' }}>
            <label className="me-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Desde</label>
            <input
              type="date"
              className="me-input"
              style={{ height: 36, fontSize: 'var(--me-text-sm)', width: 160 }}
              value={customDesde}
              onChange={e => setCustomDesde(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--me-space-2)' }}>
            <label className="me-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Hasta</label>
            <input
              type="date"
              className="me-input"
              style={{ height: 36, fontSize: 'var(--me-text-sm)', width: 160 }}
              value={customHasta}
              onChange={e => setCustomHasta(e.target.value)}
            />
          </div>
          {customDesde && customHasta && (
            <div style={{ fontSize: 'var(--me-text-xs)', color: 'var(--me-text-3)' }}>
              Mostrando {periodoStr}
            </div>
          )}
        </div>
      )}

      {/* ── KPI strip ───────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 'var(--me-space-4)', marginBottom: 'var(--me-space-6)' }}>
        <KpiCard
          label="Ventas realizadas"
          value={ventas.isLoading ? '—' : String(ventas.data?.count ?? 0)}
          sub={ventas.data
            ? `${ventas.data.mayoristas} may · ${ventas.data.count - ventas.data.mayoristas} min`
            : undefined}
        />
        <KpiCard
          label="Total vendido"
          value={ventas.isLoading ? '—' : `$${formatMoney(ventas.data?.totalMonto ?? 0)}`}
          color="ok"
          sub="cobrado en el período"
        />
        <KpiCard
          label="Ticket promedio"
          value={ventas.isLoading ? '—' : `$${formatMoney(ventas.data?.ticket ?? 0)}`}
          sub="por operación"
        />
        <KpiCard
          label="Total en compras"
          value={compras.isLoading ? '—' : `$${formatMoney(compras.data?.totalMonto ?? 0)}`}
          color={compras.data && compras.data.totalMonto > 0 ? 'bad' : undefined}
          sub={compras.data ? `${compras.data.count} órdenes` : undefined}
        />
      </div>

      {/* ── Composición min/may ─────────────────────────────────────────── */}
      {ventas.data && ventas.data.count > 0 && (
        <div className="me-card" style={{ marginBottom: 'var(--me-space-6)', padding: 'var(--me-space-5)' }}>
          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Composición del período</div>
          <h2 className="me-section-title" style={{ marginBottom: 'var(--me-space-4)' }}>Minorista vs. Mayorista</h2>
          <div style={{ display: 'flex', gap: 'var(--me-space-4)' }}>
            <div style={{
              flex: 1, padding: '12px 16px', borderRadius: 10,
              background: 'var(--me-acid-soft)', borderLeft: '3px solid var(--me-acid)',
            }}>
              <div className="me-eyebrow" style={{ color: '#4a5800', marginBottom: 'var(--me-space-2)' }}>Minorista</div>
              <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
                ${formatMoney(ventas.data.totalMinorista)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 4 }}>
                {ventas.data.count - ventas.data.mayoristas} ventas
              </div>
            </div>
            <div style={{
              flex: 1, padding: '12px 16px', borderRadius: 10,
              background: 'var(--me-mayor-soft)', borderLeft: '3px solid var(--me-mayor)',
            }}>
              <div className="me-eyebrow" style={{ color: 'var(--me-mayor)', marginBottom: 'var(--me-space-2)' }}>Mayorista</div>
              <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 22, fontWeight: 700, color: 'var(--me-mayor)', lineHeight: 1 }}>
                ${formatMoney(ventas.data.totalMayorista)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 4 }}>
                {ventas.data.mayoristas} ventas
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 2-col: medios de pago + top productos ───────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '320px 1fr',
        gap: 'var(--me-space-6)', marginBottom: 'var(--me-space-6)', alignItems: 'start',
      }}>
        {/* Medios de pago */}
        <div className="me-card">
          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Cobros del período</div>
          <h2 className="me-section-title" style={{ marginBottom: 'var(--me-space-5)' }}>Medios de pago</h2>

          {pagos.isLoading && (
            <div style={{ color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>Cargando...</div>
          )}
          {!pagos.isLoading && totalPagos === 0 && (
            <div style={{ color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>
              Sin cobros registrados en este período
            </div>
          )}
          {pagos.data && totalPagos > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--me-space-4)' }}>
              {(Object.entries(pagos.data) as [MetodoPago, number][])
                .filter(([, v]) => v > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([metodo, monto]) => {
                  const pct = Math.round((monto / totalPagos) * 100)
                  return (
                    <div key={metodo}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'baseline', marginBottom: 'var(--me-space-1)',
                      }}>
                        <span style={{ fontSize: 'var(--me-text-sm)', color: 'var(--me-text-2)', fontWeight: 500 }}>
                          {METODO_LABEL[metodo]}
                        </span>
                        <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 'var(--me-text-sm)' }}>
                          ${formatMoney(monto)}{' '}
                          <span style={{ color: 'var(--me-text-4)', fontSize: 11 }}>{pct}%</span>
                        </span>
                      </div>
                      <div style={{ height: 6, background: 'var(--me-surface-3)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${(monto / maxPago) * 100}%`,
                          background: 'var(--me-acid)',
                          borderRadius: 3,
                          transition: 'width 0.5s var(--me-ease-ui)',
                        }} />
                      </div>
                    </div>
                  )
                })}
              <div style={{
                marginTop: 'var(--me-space-2)', paddingTop: 'var(--me-space-3)',
                borderTop: '1px solid var(--me-line-2)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 'var(--me-text-xs)', color: 'var(--me-text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Total cobrado
                </span>
                <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 'var(--me-text-sm)', fontWeight: 700, color: 'var(--me-ok)' }}>
                  ${formatMoney(totalPagos)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Top 10 productos */}
        <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
          <div style={{ padding: 'var(--me-space-5)', borderBottom: '1px solid var(--me-line-2)' }}>
            <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Por volumen de venta</div>
            <h2 className="me-section-title">Top 10 productos</h2>
          </div>
          {topProductos.isLoading && (
            <div style={{ padding: 'var(--me-space-5)', color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>Cargando...</div>
          )}
          {topProductos.data?.length === 0 && (
            <div style={{ padding: 'var(--me-space-5)', color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>
              Sin ventas en este período — los productos aparecen aquí al registrar ventas
            </div>
          )}
          {topProductos.data && topProductos.data.length > 0 && (
            <table className="me-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>#</th>
                  <th>Producto</th>
                  <th style={{ textAlign: 'right' }}>Unid.</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {topProductos.data.map((p, i) => (
                  <tr key={p.producto_id} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)' }}>
                    <td style={{
                      fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                      fontSize: 'var(--me-text-xs)', color: 'var(--me-text-4)', fontWeight: 700,
                    }}>
                      {i + 1}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.nombre}</div>
                      {p.marca && (
                        <div style={{ fontSize: 'var(--me-text-xs)', color: 'var(--me-text-3)' }}>{p.marca}</div>
                      )}
                    </td>
                    <td style={{
                      textAlign: 'right', fontFamily: 'var(--me-mono)',
                      fontFeatureSettings: '"tnum"', color: 'var(--me-text-2)',
                    }}>
                      {p.unidades}
                    </td>
                    <td style={{
                      textAlign: 'right', fontFamily: 'var(--me-mono)',
                      fontFeatureSettings: '"tnum"', fontWeight: 600,
                    }}>
                      ${formatMoney(Number(p.total))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Movimientos de Caja ──────────────────────────────────────────── */}
      <div className="me-card" style={{ padding: 0, overflow: 'clip', marginBottom: 'var(--me-space-6)' }}>
        <div style={{ padding: 'var(--me-space-5)', borderBottom: '1px solid var(--me-line-2)' }}>
          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Flujo de efectivo del período</div>
          <h2 className="me-section-title">Movimientos de caja</h2>
        </div>

        {caja.isLoading && (
          <div style={{ padding: 'var(--me-space-5)', color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>
            Cargando...
          </div>
        )}

        {!caja.isLoading && caja.data?.sesiones === 0 && (
          <div style={{ padding: 'var(--me-space-5)', color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>
            Sin sesiones de caja en este período — los movimientos aparecen aquí al operar la caja
          </div>
        )}

        {caja.data && caja.data.sesiones > 0 && (
          <>
            {/* KPI strip de caja */}
            <div style={{
              display: 'flex', gap: 0,
              borderBottom: '1px solid var(--me-line-2)',
            }}>
              {[
                { label: 'Total ingresos', value: caja.data.totalIngresos, color: 'var(--me-ok)' },
                { label: 'Total egresos',  value: caja.data.totalEgresos,  color: 'var(--me-bad)' },
                { label: 'Saldo neto',     value: caja.data.saldoNeto,     color: caja.data.saldoNeto >= 0 ? 'var(--me-ok)' : 'var(--me-bad)' },
              ].map((item, i, arr) => (
                <div key={item.label} style={{
                  flex: 1, padding: '16px 20px',
                  borderRight: i < arr.length - 1 ? '1px solid var(--me-line-2)' : 'none',
                }}>
                  <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>{item.label}</div>
                  <div style={{
                    fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                    fontSize: 'var(--me-text-xl)', fontWeight: 700,
                    color: item.color, lineHeight: 1,
                  }}>
                    ${formatMoney(item.value)}
                  </div>
                </div>
              ))}
              <div style={{
                padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                borderLeft: '1px solid var(--me-line-2)', minWidth: 100,
              }}>
                <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Sesiones</div>
                <div style={{
                  fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                  fontSize: 'var(--me-text-xl)', fontWeight: 700, lineHeight: 1,
                }}>
                  {caja.data.sesiones}
                </div>
              </div>
            </div>

            {/* Desglose por concepto */}
            {caja.data.desglose.length > 0 && (
              <table className="me-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th style={{ textAlign: 'right' }}>Movimientos</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {caja.data.desglose.map((d, i) => (
                    <tr key={`${d.tipo}-${d.concepto}`} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)' }}>
                      <td>
                        {d.tipo === 'ingreso'
                          ? <span className="me-pill me-pill-ok">ingreso</span>
                          : <span className="me-pill me-pill-bad">egreso</span>}
                      </td>
                      <td style={{ fontWeight: 500, fontSize: 'var(--me-text-sm)' }}>{d.concepto}</td>
                      <td style={{
                        textAlign: 'right', fontFamily: 'var(--me-mono)',
                        fontFeatureSettings: '"tnum"', color: 'var(--me-text-3)',
                      }}>
                        {d.cantidad}
                      </td>
                      <td style={{
                        textAlign: 'right', fontFamily: 'var(--me-mono)',
                        fontFeatureSettings: '"tnum"', fontWeight: 600,
                        color: d.tipo === 'ingreso' ? 'var(--me-ok)' : 'var(--me-bad)',
                      }}>
                        ${formatMoney(d.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* ── Detalle ventas ───────────────────────────────────────────────── */}
      <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
        <div style={{
          padding: 'var(--me-space-5)', borderBottom: '1px solid var(--me-line-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Historial del período</div>
            <h2 className="me-section-title">Detalle de ventas</h2>
          </div>
          <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 'var(--me-space-3)' }}>
            {detalleVentas.data && detalleVentas.data.length > 0 && (
              <span style={{ fontSize: 'var(--me-text-xs)', color: 'var(--me-text-4)' }}>
                {detalleVentas.data.length} registros
              </span>
            )}
          </div>
        </div>

        {detalleVentas.isLoading && (
          <div style={{ padding: 'var(--me-space-5)', color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>Cargando...</div>
        )}
        {detalleVentas.data?.length === 0 && (
          <div style={{ padding: 'var(--me-space-5)', color: 'var(--me-text-3)', fontSize: 'var(--me-text-sm)' }}>
            Sin ventas registradas en este período
          </div>
        )}
        {detalleVentas.data && detalleVentas.data.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Venta</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Cobrado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {detalleVentas.data.map((v, i) => {
                  const saldo = v.total - v.total_pagado
                  return (
                    <tr key={v.id} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)' }}>
                      <td style={{
                        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                        fontWeight: 600, color: 'var(--me-text-3)',
                      }}>
                        #{v.numero}
                      </td>
                      <td>
                        {v.tipo === 'mayorista'
                          ? <span className="me-pill me-pill-mayor">mayorista</span>
                          : <span className="me-pill me-pill-neutral">minorista</span>}
                      </td>
                      <td style={{ color: 'var(--me-text-2)', fontSize: 'var(--me-text-sm)' }}>
                        {v.clientes?.nombre ?? '—'}
                      </td>
                      <td><EstadoPill estado={v.estado} /></td>
                      <td style={{
                        textAlign: 'right', fontFamily: 'var(--me-mono)',
                        fontFeatureSettings: '"tnum"', fontWeight: 600,
                      }}>
                        ${formatMoney(v.total)}
                      </td>
                      <td style={{
                        textAlign: 'right', fontFamily: 'var(--me-mono)',
                        fontFeatureSettings: '"tnum"',
                        color: saldo > 0 ? 'var(--me-bad)' : 'var(--me-ok)',
                      }}>
                        ${formatMoney(v.total_pagado)}
                      </td>
                      <td style={{
                        fontSize: 'var(--me-text-sm)', color: 'var(--me-text-3)',
                        whiteSpace: 'nowrap', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                      }}>
                        {formatFechaCorta(v.created_at)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
